import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { mockData } from '../data/mock';

const GAP = 24;
const DURATION = 420;

/* ── Helpers ── */
const Avatar = ({ name }) => {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const colors = ['#4285F4', '#34A853', '#FBBC05', '#EA4335', '#70AD25'];
  return <div className="t-avatar" aria-hidden="true" style={{ background: colors[name.charCodeAt(0) % colors.length] }}>{initials}</div>;
};

const Stars = ({ rating }) => (
  <div className="t-stars" role="img" aria-label={`${rating} étoiles sur 5`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={15} fill={i < rating ? '#FBBC05' : 'none'} stroke={i < rating ? '#FBBC05' : '#93A29D'} />
    ))}
  </div>
);

const GoogleBadge = () => (
  <svg className="t-google-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/* ── Responsive ── */
const useVisible = () => {
  const [v, setV] = useState(3);
  useEffect(() => {
    const upd = () => setV(window.innerWidth < 640 ? 1 : window.innerWidth < 960 ? 2 : 3);
    upd();
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);
  return v;
};

/* ── Component ── */
const Testimonials = () => {
  const reviews = mockData.testimonials;
  const n = reviews.length;
  const visible = useVisible();

  // Build cloned array for infinite loop
  const cloned = [...reviews.slice(-visible), ...reviews, ...reviews.slice(0, visible)];

  const viewportRef  = useRef(null);
  const stepRef      = useRef(0);
  const lockRef      = useRef(false);
  const timerRef     = useRef(null);
  const [cardWidth, setCardWidth] = useState(0); // state → triggers re-render with correct card size

  const [idx, setIdx]         = useState(visible);
  const [transOn, setTransOn] = useState(false); // controls CSS transition

  /* Measure card width → step */
  const measure = useCallback(() => {
    if (!viewportRef.current) return;
    const w = viewportRef.current.offsetWidth;
    if (w === 0) return;
    const step = (w - GAP * (visible - 1)) / visible + GAP;
    stepRef.current = step;
    setCardWidth(step - GAP); // triggers re-render so card widths update
  }, [visible]);

  /* Re-measure on mount, resize, visible change */
  useEffect(() => {
    measure();
    setIdx(visible);
    setTransOn(false);
    lockRef.current = false;
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); clearTimeout(timerRef.current); };
  }, [measure, visible]);

  /* Navigate */
  const go = useCallback((dir) => {
    if (lockRef.current) return;
    measure(); // always re-measure (handles resize edge case)
    lockRef.current = true;

    setTransOn(true);
    setIdx(i => {
      const next = i + dir;
      return next;
    });

    timerRef.current = setTimeout(() => {
      // After transition: silent jump to equivalent real position if in clone zone
      setTransOn(false);
      setIdx(current => {
        const realEnd = visible + n;
        if (current >= realEnd)       return visible;           // wrap forward
        if (current < visible)        return visible + n - 1;  // wrap backward
        return current;                                         // no jump needed
      });
      // Unlock after DOM paint (tiny delay prevents double-fire)
      requestAnimationFrame(() => { lockRef.current = false; });
    }, DURATION + 20);
  }, [measure, n, visible]);

  /* Dot index (0..n-1) */
  const dotIdx = ((idx - visible) % n + n) % n;

  /* Pixel offset of the track */
  const translateX = idx * stepRef.current;

  return (
    <section id="temoignages" className="section-testimonials">
      <div className="container">

        {/* Header */}
        <div className="section-header" data-aos="fade-up">
          <h2 className="section-title">Avis de nos clients</h2>
          <p className="section-subtitle">Ce que nos clients disent de nous sur Google</p>
        </div>

        {/* Carousel */}
        <div className="t-slider" data-aos="fade-up" data-aos-delay="80">

          <button className="t-nav" onClick={() => go(-1)} aria-label="Précédent">
            <ChevronLeft size={22} />
          </button>

          <div className="t-viewport" ref={viewportRef}>
            <div
              className="t-track"
              style={{
                display: 'flex',
                gap: `${GAP}px`,
                transition: transOn ? `transform ${DURATION}ms cubic-bezier(0.4,0,0.2,1)` : 'none',
                transform: `translateX(-${translateX}px)`,
              }}
            >
              {cloned.map((review, i) => {
                  return (
                  <div
                    key={`${review.id}-${i}`}
                    className="testimonial-card"
                    style={cardWidth > 0 ? { flex: `0 0 ${cardWidth}px`, minWidth: `${cardWidth}px` } : {}}
                  >
                    <div className="t-card-header">
                      <Avatar name={review.name} />
                      <div className="t-card-meta">
                        <div className="t-card-name">{review.name}</div>
                        <div className="t-card-date">{review.date}</div>
                      </div>
                      <GoogleBadge />
                    </div>
                    <Stars rating={review.rating} />
                    <p className="testimonial-content">"{review.content}"</p>
                  </div>
                );
              })}
            </div>
          </div>

          <button className="t-nav" onClick={() => go(1)} aria-label="Suivant">
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Dots */}
        <div className="t-dots">
          {reviews.map((_, i) => (
            <button
              key={i}
              className={`t-dot${i === dotIdx ? ' t-dot--active' : ''}`}
              onClick={() => {
                if (!lockRef.current) {
                  const diff = i - dotIdx;
                  if (diff !== 0) go(diff > 0 ? 1 : -1);
                }
              }}
              aria-label={`Avis ${i + 1}`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
